import React, { useState, useRef, useEffect } from 'react';
import { Link, useNavigate, useLocation } from 'react-router-dom';
import { useContext } from 'react';
import { AuthContext } from '../../context/AuthContext';
import ThemeToggle from '../common/ThemeToggle';
import appIcon from '../../assets/app_icon.png';
import { 
  Bars3Icon, 
  XMarkIcon,
  HomeIcon,
  BookOpenIcon,
  AcademicCapIcon,
  DocumentTextIcon,
  QuestionMarkCircleIcon,
  Cog6ToothIcon,
} from '@heroicons/react/24/outline';

const Navbar = () => {
  const [isOpen, setIsOpen] = useState(false);
  const dropdownRef = useRef(null);
  const { logout } = useContext(AuthContext);
  const navigate = useNavigate();
  const location = useLocation();

  // Handle click outside to close dropdown
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target)) {
        setIsOpen(false);
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  const handleLogout = () => {
    logout();
    navigate('/');
  };

  const menuItems = [
    { path: '/home', label: 'Startseite', icon: HomeIcon },
    { path: '/micro-learning', label: 'Lernen nach Thema', icon: BookOpenIcon },
    { path: '/pruefungsboegen', label: 'Prüfungsbögen', icon: DocumentTextIcon },
    { path: '/wiki', label: 'Wiki', icon: AcademicCapIcon },
    { path: '/alle-fragen', label: 'Alle Fragen', icon: QuestionMarkCircleIcon },
    { path: '/settings', label: 'Einstellungen', icon: Cog6ToothIcon },
  ];

  const isActive = (path) => location.pathname === path;

  return (
    <div className="sticky top-0 z-50">
      <div className="navbar bg-base-100 shadow-lg">
        {/* Mobile Menu Button */}
        <div className="flex-none lg:hidden">
          <div className="dropdown" ref={dropdownRef}>
            <label 
              tabIndex={0} 
              className="btn btn-ghost"
              onClick={() => setIsOpen(!isOpen)}
            >
              {isOpen ? (
                <XMarkIcon className="h-6 w-6" />
              ) : (
                <Bars3Icon className="h-6 w-6" />
              )}
            </label>
            {isOpen && (
              <ul tabIndex={0} className="menu menu-sm dropdown-content mt-3 z-[1] p-2 shadow-lg bg-base-100 rounded-lg w-60 gap-1">
                {menuItems.map((item) => {
                  const Icon = item.icon;
                  return (
                    <li key={item.path}>
                      <Link 
                        to={item.path} 
                        className={`flex items-center gap-3 p-3 hover:bg-primary hover:text-primary-content rounded-lg transition-colors
                          ${isActive(item.path) ? 'bg-primary/10 text-primary' : 'text-base-content'}`}
                        onClick={() => setIsOpen(false)}
                      >
                        <Icon className="w-5 h-5" />
                        {item.label}
                      </Link>
                    </li>
                  );
                })}
                <div className="divider my-1"></div>
                <li>
                  <button 
                    onClick={() => {
                      handleLogout();
                      setIsOpen(false);
                    }} 
                    className="flex items-center gap-3 p-3 text-error hover:bg-error hover:text-error-content rounded-lg transition-colors"
                  >
                    <XMarkIcon className="w-5 h-5" />
                    Logout
                  </button>
                </li>
              </ul>
            )}
          </div>
        </div>

        {/* Logo - Mobile (Centered) */}
        <div className="flex-1 lg:hidden flex justify-center items-center">
          <div className="absolute left-1/2 transform -translate-x-1/2">
            <Link to="/home" className="btn btn-ghost normal-case text-xl font-bold gap-2">
              <img src={appIcon} alt="Mein-SKS Logo" className="w-8 h-8" />
              Mein-SKS
            </Link>
          </div>
        </div>

        {/* Logo - Desktop (Left aligned) */}
        <div className="navbar-start hidden lg:flex">
          <Link to="/home" className="btn btn-ghost normal-case text-xl font-bold gap-2">
            <img src={appIcon} alt="Mein-SKS Logo" className="w-8 h-8" />
            Mein-SKS
          </Link>
        </div>

        {/* Desktop Menu */}
        <div className="navbar-center hidden lg:flex">
          <ul className="menu menu-horizontal px-1 gap-1">
            {menuItems.map((item) => {
              const Icon = item.icon;
              return (
                <li key={item.path}>
                  <Link 
                    to={item.path} 
                    className={`btn btn-sm normal-case gap-2 ${
                      isActive(item.path) 
                        ? 'btn-primary text-white' 
                        : 'btn-ghost hover:btn-primary hover:text-white'
                    }`}
                  >
                    <Icon className="w-4 h-4" />
                    {item.label}
                  </Link>
                </li>
              );
            })}
          </ul>
        </div>

        {/* Right Section */}
        <div className="navbar-end gap-2">
          <ThemeToggle />
          <button 
            onClick={handleLogout} 
            className="btn btn-sm btn-error text-error-content normal-case hidden lg:flex"
          >
            Logout
          </button>
        </div>
      </div>
    </div>
  );
};

export default Navbar;